import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { apiClient } from '@/lib/api/client'
import type { AdminPaymentDevice, Organization } from '@/lib/api/types'
import { useAuth } from '@/lib/auth'

export const Route = createFileRoute('/_authenticated/ops/terminals')({
  // Superuser gating lives on the /ops layout route (ops.tsx);
  // every endpoint here is additionally superuser-enforced server-side.
  component: TerminalsPage,
})

const inputCls =
  'block w-full mt-1 px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100'
const labelCls = 'text-xs font-medium text-neutral-500 dark:text-neutral-400'

/**
 * Type-to-search picker. The selected org id travels through the form as a
 * hidden input named `organization`, so FormData-based submit handlers keep
 * working; an unpicked/typed-only value submits as ''.
 */
function OrgCombobox({ organizations, defaultOrgId }: {
  organizations: Organization[]
  defaultOrgId?: number
}) {
  const initial = organizations.find((org) => org.id === defaultOrgId)
  const [query, setQuery] = useState(initial?.name ?? '')
  const [selectedId, setSelectedId] = useState<number | ''>(initial?.id ?? '')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)

  const q = query.trim().toLowerCase()
  const selected = organizations.find((org) => org.id === selectedId)
  // Once a pick is made, the input holds its full name — filtering on that
  // would pin the list to one entry, so show everything again instead.
  const matches = !q || selected?.name.toLowerCase() === q
    ? organizations
    : organizations.filter((org) => org.name.toLowerCase().includes(q))

  const pick = (org: Organization) => {
    setSelectedId(org.id)
    setQuery(org.name)
    setOpen(false)
  }

  return (
    <div className="relative">
      <input
        value={query}
        required
        placeholder="Type to search…"
        className={inputCls}
        onChange={(e) => {
          setQuery(e.target.value)
          setSelectedId('')
          setOpen(true)
          setHighlight(0)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
            setHighlight((h) => Math.min(h + 1, matches.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlight((h) => Math.max(h - 1, 0))
          } else if (e.key === 'Enter' && open && matches[highlight]) {
            e.preventDefault()
            pick(matches[highlight])
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
      />
      <input type="hidden" name="organization" value={selectedId} />
      {open && matches.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg py-1">
          {matches.map((org, index) => (
            <li
              key={org.id}
              // mousedown fires before the input's blur closes the list
              onMouseDown={(e) => { e.preventDefault(); pick(org) }}
              onMouseEnter={() => setHighlight(index)}
              className={`px-3 py-1.5 text-sm cursor-pointer text-neutral-900 dark:text-neutral-100 ${
                index === highlight ? 'bg-neutral-100 dark:bg-neutral-800' : ''
              } ${org.id === selectedId ? 'font-medium' : ''}`}
            >
              {org.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function TerminalsPage() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [showProvision, setShowProvision] = useState(false)
  const [editDevice, setEditDevice] = useState<AdminPaymentDevice | null>(null)
  const [actionError, setActionError] = useState('')
  const [search, setSearch] = useState('')
  const [orgFilter, setOrgFilter] = useState('')

  const { data: devices, isLoading } = useQuery({
    queryKey: ['ops-payment-devices'],
    queryFn: () => apiClient.listPaymentDevices(token ?? ''),
    enabled: !!token,
  })
  const { data: organizations } = useQuery({
    queryKey: ['ops-organizations'],
    queryFn: () => apiClient.getOrganizationsList(token ?? ''),
    enabled: !!token,
  })

  const orgNames = useMemo(
    () =>
      [...new Set((devices ?? []).map((d) => d.organization_name).filter((n): n is string => !!n))].sort(),
    [devices],
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (devices ?? []).filter((device) => {
      if (orgFilter && device.organization_name !== orgFilter) return false
      if (!q) return true
      return [
        device.name, device.external_id, device.serial_number,
        device.merchant_id, device.device_model, device.organization_name,
      ].some((value) => value?.toLowerCase().includes(q))
    })
  }, [devices, search, orgFilter])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ops-payment-devices'] })

  const runAction = async (fn: () => Promise<unknown>) => {
    setActionError('')
    try {
      await fn()
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Action failed')
    } finally {
      invalidate()
    }
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Terminals</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Card-present payment devices across all organizations. Events see only the
            terminals matching their own merchant account and environment.
          </p>
        </div>
        <button
          onClick={() => setShowProvision(true)}
          className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
        >
          Provision Terminal
        </button>
      </div>

      {actionError && (
        <p className="mb-4 text-sm text-red-600 dark:text-red-400">{actionError}</p>
      )}

      {!!devices?.length && (
        <div className="flex flex-wrap gap-3 mb-4">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, serial, merchant ID…"
            className="w-72 max-w-full px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100"
          />
          {orgNames.length > 1 && (
            <select
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100"
            >
              <option value="">All organizations</option>
              {orgNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 overflow-x-auto">
        {isLoading ? (
          <p className="px-5 py-8 text-sm text-neutral-500 dark:text-neutral-400">Loading…</p>
        ) : !devices || devices.length === 0 ? (
          <p className="px-5 py-8 text-sm text-neutral-500 dark:text-neutral-400">
            No terminals provisioned yet.
          </p>
        ) : filtered.length === 0 ? (
          <p className="px-5 py-8 text-sm text-neutral-500 dark:text-neutral-400">
            No terminals match the current filter.
          </p>
        ) : (
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-800">
                <th className="text-left font-medium px-5 py-3">Name</th>
                <th className="text-left font-medium px-3 py-3">Organization</th>
                <th className="text-left font-medium px-3 py-3">Model</th>
                <th className="text-left font-medium px-3 py-3">Serial</th>
                <th className="text-left font-medium px-3 py-3">Merchant ID</th>
                <th className="text-left font-medium px-3 py-3">Env</th>
                <th className="text-left font-medium px-3 py-3">Status</th>
                <th className="text-left font-medium px-3 py-3">Connection</th>
                <th className="text-right font-medium px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((device) => (
                <DeviceRow
                  key={device.id}
                  device={device}
                  runAction={runAction}
                  onEdit={() => setEditDevice(device)}
                  token={token ?? ''}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showProvision && (
        <ProvisionModal
          organizations={organizations ?? []}
          onClose={() => setShowProvision(false)}
          onDone={() => { setShowProvision(false); invalidate() }}
          onError={(message) => setActionError(message)}
          token={token ?? ''}
        />
      )}

      {editDevice && (
        <EditDeviceModal
          device={editDevice}
          organizations={organizations ?? []}
          onClose={() => setEditDevice(null)}
          onDone={() => { setEditDevice(null); invalidate() }}
          token={token ?? ''}
        />
      )}
    </div>
  )
}

function DeviceRow({ device, runAction, onEdit, token }: {
  device: AdminPaymentDevice
  runAction: (fn: () => Promise<unknown>) => Promise<void>
  onEdit: () => void
  token: string
}) {
  const provisioned = !!device.external_id
  return (
    <tr
      onClick={onEdit}
      className="border-b border-neutral-100 dark:border-neutral-800 last:border-0 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
    >
      <td className="px-5 py-3">
        <span className="font-medium text-neutral-900 dark:text-white">{device.name}</span>
        {device.external_id && (
          <span className="block text-xs text-neutral-400 dark:text-neutral-500 font-mono">
            {device.external_id}
          </span>
        )}
      </td>
      <td className="px-3 py-3 text-neutral-600 dark:text-neutral-400">
        {device.organization_name || '—'}
      </td>
      <td className="px-3 py-3 text-neutral-600 dark:text-neutral-400">{device.device_model}</td>
      <td className="px-3 py-3 text-neutral-600 dark:text-neutral-400 font-mono text-xs">
        {device.serial_number || '—'}
      </td>
      <td className="px-3 py-3 text-neutral-600 dark:text-neutral-400 font-mono text-xs">
        {device.merchant_id}
      </td>
      <td className="px-3 py-3">
        <span className={device.environment === 'prod'
          ? 'text-green-700 dark:text-green-400 font-medium'
          : 'text-neutral-500 dark:text-neutral-400'}>
          {device.environment}
        </span>
      </td>
      <td className="px-3 py-3">
        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
          !device.enabled
            ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
            : device.status === 'active'
              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
              : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'
        }`}>
          {device.enabled ? device.status : 'disabled'}
        </span>
      </td>
      <td className="px-3 py-3">
        {(() => {
          const connection = (device.last_connection || '').toLowerCase()
          const cls = connection === 'open'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
            : connection === 'closed'
              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
              : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'
          return (
            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${cls}`}>
              {device.last_connection || 'unknown'}
            </span>
          )
        })()}
      </td>
      <td
        className="px-5 py-3 text-right space-x-3 text-xs font-medium"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="text-neutral-600 dark:text-neutral-400 hover:underline" onClick={onEdit}>
          Edit
        </button>
        {!provisioned && (
          <button
            className="text-blue-600 dark:text-blue-400 hover:underline"
            onClick={() => runAction(() => apiClient.provisionPaymentDevice(token, device.id))}
          >
            Provision
          </button>
        )}
        {provisioned && device.status !== 'active' && (
          <button
            className="text-blue-600 dark:text-blue-400 hover:underline"
            onClick={() => runAction(() => apiClient.activatePaymentDevice(token, device.id))}
          >
            Activate
          </button>
        )}
        {provisioned && (
          <button
            className="text-neutral-600 dark:text-neutral-400 hover:underline"
            onClick={() => runAction(() => apiClient.checkPaymentDeviceConnection(token, device.id))}
          >
            Check
          </button>
        )}
      </td>
    </tr>
  )
}

function EditDeviceModal({ device, organizations, onClose, onDone, token }: {
  device: AdminPaymentDevice
  organizations: Organization[]
  onClose: () => void
  onDone: () => void
  token: string
}) {
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  // SDK-driven readers (PAX D135) need config.sdk for the mobile SDK; they
  // have no idle screen, so the idle-message field is hidden for them.
  const isSdkReader =
    (device.capabilities as Record<string, unknown> | undefined)?.interaction === 'sdk_local'
    || device.device_model.toUpperCase().includes('D135')
  const sdkConfig = ((device.config?.sdk ?? {}) as Record<string, string>)

  const submit = async (formData: FormData) => {
    const orgId = Number(formData.get('organization'))
    if (!orgId) {
      setError('Pick an organization from the list')
      return
    }
    setError('')
    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        organization: orgId,
        name: formData.get('name'),
        description: formData.get('description') || '',
        device_model: formData.get('device_model'),
        serial_number: formData.get('serial_number') || '',
        merchant_id: formData.get('merchant_id'),
        environment: formData.get('environment'),
        enabled: formData.get('enabled') === 'on',
      }
      if (isSdkReader) {
        const entered = Object.fromEntries(
          (['username', 'password', 'application', 'mid'] as const)
            .map((key) => [key, String(formData.get(`sdk_${key}`) ?? '').trim()])
            .filter(([, value]) => value),
        )
        // Merge at BOTH levels, never replace: config carries the
        // provider's provisioning data, and config.sdk may hold values this
        // form doesn't know. A blank field keeps the stored value (that's
        // how the never-re-rendered password survives an unrelated edit).
        body.config = {
          ...(device.config ?? {}),
          sdk: { ...((device.config?.sdk as Record<string, unknown>) ?? {}), ...entered },
        }
      }
      await apiClient.updatePaymentDevice(token, device.id, body)
      const idleMessage = String(formData.get('idle_message') ?? '').trim()
      if (idleMessage && device.external_id && !isSdkReader) {
        await apiClient.setPaymentDeviceIdleMessage(token, device.id, idleMessage)
      }
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
          Edit Terminal
        </h3>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-5">
          {device.external_id
            ? <span className="font-mono">{device.external_id}</span>
            : 'Not provisioned yet'}
        </p>
        <form
          onSubmit={(e) => { e.preventDefault(); submit(new FormData(e.currentTarget)) }}
          className="space-y-4"
        >
          <label className={labelCls}>
            Organization
            <OrgCombobox organizations={organizations} defaultOrgId={device.organization} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className={labelCls}>
              Name
              <input name="name" required defaultValue={device.name} className={inputCls} />
            </label>
            <label className={labelCls}>
              Model
              <input name="device_model" required defaultValue={device.device_model} className={inputCls} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className={labelCls}>
              Serial number
              <input name="serial_number" defaultValue={device.serial_number ?? ''} className={inputCls} />
            </label>
            <label className={labelCls}>
              Environment
              <select name="environment" required defaultValue={device.environment} className={inputCls}>
                <option value="prod">Production</option>
                <option value="sandbox">Sandbox</option>
              </select>
            </label>
          </div>
          <label className={labelCls}>
            Merchant ID
            <input name="merchant_id" required defaultValue={device.merchant_id} className={inputCls} />
            <span className="block mt-1 text-[10px] text-neutral-400 dark:text-neutral-500">
              Merchant ID and environment control which events can use this terminal.
            </span>
          </label>
          <label className={labelCls}>
            Description
            <input name="description" defaultValue={device.description ?? ''} className={inputCls} />
          </label>
          {isSdkReader && (
            <div>
              <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
                SDK reader settings
              </p>
              <div className="grid grid-cols-2 gap-3">
                <label className={labelCls}>
                  SDK username
                  <input name="sdk_username" defaultValue={sdkConfig.username ?? ''} className={inputCls} />
                </label>
                <label className={labelCls}>
                  SDK password
                  {/* Never re-rendered into the DOM; blank keeps the stored value. */}
                  <input
                    name="sdk_password"
                    type="password"
                    placeholder={sdkConfig.password ? '•••••••• (unchanged)' : ''}
                    className={inputCls}
                  />
                </label>
                <label className={labelCls}>
                  Application (AP…)
                  <input name="sdk_application" defaultValue={sdkConfig.application ?? ''} className={inputCls} />
                </label>
                <label className={labelCls}>
                  Processor MID
                  <input name="sdk_mid" defaultValue={sdkConfig.mid ?? ''} className={inputCls} />
                </label>
              </div>
              <span className="block mt-1 text-[10px] text-neutral-400 dark:text-neutral-500">
                Dedicated ROLE_MERCHANT credentials the mobile SDK uses to drive this
                reader — delivered to staff sessions at charge time, never stored in the app.
              </span>
            </div>
          )}
          {device.external_id && !isSdkReader && (
            <label className={labelCls}>
              Idle screen message
              <input name="idle_message" placeholder="EventPay" className={inputCls} />
              <span className="block mt-1 text-[10px] text-neutral-400 dark:text-neutral-500">
                Sent to the device immediately when filled in.
              </span>
            </label>
          )}
          <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
            <input
              type="checkbox"
              name="enabled"
              defaultChecked={device.enabled}
              className="mt-0.5 rounded border-neutral-300 dark:border-neutral-700"
            />
            <span className="text-sm text-neutral-700 dark:text-neutral-300">
              Terminal enabled
              <span className="block text-[10px] text-neutral-400 dark:text-neutral-500">
                Disabled terminals disappear from event reload dropdowns. Applies on Save.
              </span>
            </span>
          </label>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-md border border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ProvisionModal({ organizations, onClose, onDone, onError, token }: {
  organizations: Organization[]
  onClose: () => void
  onDone: () => void
  onError: (message: string) => void
  token: string
}) {
  const [formError, setFormError] = useState('')
  const provision = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.createPaymentDevice(token, body),
  })

  const submit = async (formData: FormData) => {
    const orgId = Number(formData.get('organization'))
    if (!orgId) {
      setFormError('Pick an organization from the list')
      return
    }
    setFormError('')
    try {
      await provision.mutateAsync({
        organization: orgId,
        name: formData.get('name'),
        description: formData.get('description') || '',
        device_model: formData.get('device_model'),
        serial_number: formData.get('serial_number') || '',
        merchant_id: formData.get('merchant_id'),
        environment: formData.get('environment'),
      })
      onDone()
    } catch (error) {
      // The row may have persisted with provisioning failed — the list's
      // Provision action is the retry path. Surface the provider's message.
      onError(error instanceof Error ? error.message : 'Provisioning failed')
      onDone()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-5">
          Provision Terminal
        </h3>
        <form
          onSubmit={(e) => { e.preventDefault(); submit(new FormData(e.currentTarget)) }}
          className="space-y-4"
        >
          <label className={labelCls}>
            Organization
            <OrgCombobox organizations={organizations} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className={labelCls}>
              Name
              <input name="name" required className={inputCls} placeholder="Front Gate 1" />
            </label>
            <label className={labelCls}>
              Model
              <input name="device_model" required className={inputCls} placeholder="PAX A920Pro" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className={labelCls}>
              Serial number
              <input name="serial_number" className={inputCls} />
            </label>
            <label className={labelCls}>
              Environment
              <select name="environment" required className={inputCls} defaultValue="prod">
                <option value="prod">Production</option>
                <option value="sandbox">Sandbox</option>
              </select>
            </label>
          </div>
          <label className={labelCls}>
            Merchant ID
            <input name="merchant_id" required className={inputCls} placeholder="MU…" />
            <span className="block mt-1 text-[10px] text-neutral-400 dark:text-neutral-500">
              Must match the event&apos;s Finix merchant account for the terminal to appear there.
            </span>
          </label>
          <label className={labelCls}>
            Description
            <input name="description" className={inputCls} />
          </label>
          {formError && <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-md border border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300">
              Cancel
            </button>
            <button type="submit" disabled={provision.isPending}
              className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {provision.isPending ? 'Provisioning…' : 'Provision'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
